import * as L from "leaflet";
import { TileLayerOptions } from "leaflet";
import { modify } from "./Utils";
import { Kernel } from "./Kernel";

export type MapType = "satellite" | "satellite-google" | "terrain" | "street";

export const AllMapTypes: MapType[] = ["satellite-google", "satellite", "terrain", "street"];

export type TileLayerDefinition = {
    name: string;
    url: string;
    options: TileLayerOptions;
};

export const MIN_ZOOM = 3;
export const MAX_ZOOM = 22;

export enum GoogleTileType {
    RoadsOnly = "h",
    StandardRoadmap = "m",
    Terrain = "p",
    AlteredRoadmap = "r",
    SatelliteOnly = "s",
    TerrainOnly = "t",
    Hybrid = "y",
}

function makeLayerOptions(props: any) {
    return modify(
        {
            tileSize: 256,
            detectRetina: true,
            minZoom: MIN_ZOOM,
            maxZoom: MAX_ZOOM,
        },
        props
    );
}

function makeLayerDefinition(name: string, url: string, options: any) {
    return {
        name,
        url,
        options: makeLayerOptions(options),
    };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function makeGoogleTileLayer(name: string, layers: GoogleTileType[]): TileLayerDefinition {
    return makeLayerDefinition(
        name,
        `https://{s}.google.com/vt/lyrs=${layers.join(",")}&x={x}&y={y}&z={z}`,
        {
            subdomains: ["mt0", "mt1", "mt2", "mt3"],
            attribution: `&copy; Map data ©2019 Google`,
            maxNativeZoom: 19,
        }
    );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function makeOSMTileLayer() {
    return makeLayerDefinition(
        "Street map",
        "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
        {
            maxNativeZoom: 19,
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }
    );
}

function makeESRILayer(name: string, map: string) {
    return makeLayerDefinition(
        name,
        `https://server.arcgisonline.com/ArcGIS/rest/services/${map}/MapServer/tile/{z}/{y}/{x}`,
        {
            attribution: "Tiles &copy; Esri &mdash; Esri",
            maxNativeZoom: 16,
        }
    );
}

export const TileLayerDefinitions = new Map<MapType, TileLayerDefinition>([
    ["satellite-google", makeGoogleTileLayer("Google Satellite", [GoogleTileType.Hybrid])],
    ["satellite", makeESRILayer("ESRI Satellite", "World_Imagery")],
    ["terrain", makeESRILayer("ESRI Terrain", "World_Topo_Map")],
    ["street", makeESRILayer("ESRI Street Map", "World_Street_Map")],
]);

class LeafletTileCache {
    async get(url: string, done: any) {
        try {
            const metadata = await Kernel.instance().tileCache.getTileMetadata(url);
            if (metadata) {
                const data = await Kernel.instance().tileCache.getTileImage(url);
                if (data) {
                    done(undefined, {
                        timestamp: metadata.timestamp,
                        image: data.image,
                    });
                }
            } else {
                done(`error`, undefined);
            }
        } catch (error) {
            done(error, undefined);
        }
    }

    async put(
        url: string,
        _existingRevision: string,
        timestamp: number,
        _format: string,
        image: Blob
    ) {
        Kernel.instance().tileCache.putTile(url, timestamp, image);
    }
}

export function createTileLayer(type: MapType) {
    const def = TileLayerDefinitions.get(type) as TileLayerDefinition;
    return L.tileLayer(
        def.url,
        modify(def.options, {
            useCache: Kernel.instance().enableTileCache.get(),
            cacheDb: new LeafletTileCache(),
            crossOrigin: true,
            cacheMaxAge: 180 * 24 * 3600 * 1000,
        })
    );
}
